import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Pencil,
  Power,
  PowerOff,
  Plus,
  Bot,
  FileText,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import AdminLayout from "@/components/layout/AdminLayout";
import { useToast } from "@/hooks/use-toast";
import { fetchAiPersona, toggleAiPersonaActive } from "@/api/aiPersonas";
import { fetchAiPromptTemplates } from "@/api/aiPromptTemplates";
import { AiPersona } from "@/types/aiPersona";
import { AiPromptTemplate, AI_PROMPT_PURPOSES } from "@/types/aiPromptTemplate";

const AiPersonaDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [persona, setPersona] = useState<AiPersona | null>(null);
  const [templates, setTemplates] = useState<AiPromptTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [personaData, allTemplates] = await Promise.all([
        fetchAiPersona(Number(id)),
        fetchAiPromptTemplates(),
      ]);

      if (!personaData) {
        toast({
          title: "Not Found",
          description: "Persona not found.",
          variant: "destructive",
        });
        navigate("/admin/ai/personas");
        return;
      }

      setPersona(personaData);
      setTemplates(
        allTemplates.filter((t) => t.personaId === personaData.id)
      );
    } catch (error) {
      console.error("Error loading persona:", error);
      toast({
        title: "Error",
        description: "Failed to load persona details.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    if (!persona) return;
    try {
      await toggleAiPersonaActive(persona.id, !persona.isActive);
      toast({
        title: persona.isActive ? "Deactivated" : "Activated",
        description: `"${persona.name}" is now ${persona.isActive ? "inactive" : "active"}.`,
      });
      await loadData();
    } catch (error) {
      console.error("Error toggling persona:", error);
      toast({
        title: "Error",
        description: "Failed to update persona status.",
        variant: "destructive",
      });
    }
  };

  const getPurposeLabel = (purpose: string) =>
    AI_PROMPT_PURPOSES.find((p) => p.value === purpose)?.label || purpose;

  if (loading) {
    return (
      <AdminLayout title="AI Persona">
        <div className="flex h-64 w-full items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      </AdminLayout>
    );
  }

  if (!persona) return null;

  return (
    <AdminLayout title={persona.name}>
      <div className="space-y-6 max-w-4xl">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/admin/ai/personas")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Personas
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild>
              <Link to={`/admin/ai/personas/${persona.id}/edit`}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </Link>
            </Button>
            <Button variant="outline" onClick={handleToggle}>
              {persona.isActive ? (
                <PowerOff className="h-4 w-4 mr-2" />
              ) : (
                <Power className="h-4 w-4 mr-2" />
              )}
              {persona.isActive ? "Deactivate" : "Activate"}
            </Button>
          </div>
        </div>

        {/* Persona Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5" />
              Persona Details
              <Badge
                variant={persona.isActive ? "default" : "secondary"}
                className="ml-2"
              >
                {persona.isActive ? "Active" : "Inactive"}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Name
                </p>
                <p>{persona.name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Slug
                </p>
                <code className="text-sm bg-muted px-1.5 py-0.5 rounded">
                  {persona.slug}
                </code>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-muted-foreground">
                  Description
                </p>
                <p>{persona.description || "—"}</p>
              </div>
              {persona.avatarUrl && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Avatar URL
                  </p>
                  <p className="text-sm break-all">{persona.avatarUrl}</p>
                </div>
              )}
              {persona.defaultModel && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Default Model
                  </p>
                  <p>{persona.defaultModel}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Configuration */}
        {Object.keys(persona.metadata).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Configuration</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {persona.metadata.temperature !== undefined && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Temperature
                    </p>
                    <p>{persona.metadata.temperature}</p>
                  </div>
                )}
                {persona.metadata.max_tokens !== undefined && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Max Tokens
                    </p>
                    <p>{persona.metadata.max_tokens}</p>
                  </div>
                )}
                {persona.metadata.use_case && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Use Case
                    </p>
                    <code className="text-sm bg-muted px-1.5 py-0.5 rounded">
                      {persona.metadata.use_case as string}
                    </code>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Dates */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-medium text-muted-foreground">Created</p>
                <p>{new Date(persona.createdAt).toLocaleString()}</p>
              </div>
              <div>
                <p className="font-medium text-muted-foreground">Updated</p>
                <p>{new Date(persona.updatedAt).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Linked Templates */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Linked Templates ({templates.length})
              </span>
              <Button size="sm" asChild>
                <Link
                  to={`/admin/ai/prompt-templates/new?personaId=${persona.id}`}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Template
                </Link>
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {templates.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Purpose</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell className="font-medium">
                        {template.name}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {getPurposeLabel(template.purpose)}
                        </Badge>
                      </TableCell>
                      <TableCell>v{template.version}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            template.isActive ? "default" : "secondary"
                          }
                        >
                          {template.isActive ? "Active" : "Draft"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" asChild>
                          <Link
                            to={`/admin/ai/prompt-templates/${template.id}/edit`}
                          >
                            <Pencil className="h-3 w-3 mr-1" />
                            Edit
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-6">
                <FileText className="mx-auto h-10 w-10 text-muted-foreground" />
                <p className="mt-2 text-sm text-muted-foreground">
                  No templates linked to this persona yet.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AiPersonaDetail;
